// premium-pricing: transparent models with clear value props
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Monitor, Users, Zap, Settings, Bot } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

// Set document title and meta tags for Pricing page
const setPricingPageMeta = () => {
  document.title = "Pricing - AffinityAim | Annual Plans for Websites, Chatbots & Custom Apps";
  
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', 'Simple, transparent annual pricing for websites ($500-$1,000/year), AI chatbots ($500/year), and custom app development. No hidden fees.');
  }
  
  const metaKeywords = document.querySelector('meta[name="keywords"]');
  if (metaKeywords) {
    metaKeywords.setAttribute('content', 'website pricing, annual website plans, chatbot pricing, web app development cost, custom app pricing, website as a service pricing');
  }
};

const pricingPlans = [
  {
    name: "Website Essentials",
    price: "$500/year",
    priceValue: "Website Essentials",
    icon: Monitor,
    bestFor: "Small businesses that want a professional website without the headache",
    description: "Everything you need for a professional web presence",
    features: [
      "Hosting + SSL + uptime monitoring",
      "Up to 6 pages (Home, About, Services, Contact, +2 custom)",
      "Responsive design, GA4, on-page SEO basics, cookie banner",
      "Content updates up to 1h/month"
    ],
    notIncluded: null,
    cta: "Choose Essentials"
  },
  {
    name: "Growth Website + SEO",
    price: "$800/year", 
    priceValue: "Growth Website + SEO",
    icon: Zap,
    bestFor: "Teams who need monthly growth and measurable improvements",
    description: "Everything in Essentials plus monthly SEO optimization",
    features: [
      "Everything in Essentials",
      "Monthly SEO tasks (technical fixes, content tweaks, internal links)",
      "1 landing page/quarter",
      "Core Web Vitals checks",
      "2h updates/month"
    ],
    notIncluded: null,
    cta: "Choose Growth + SEO"
  },
  {
    name: "AI Chatbot",
    price: "$500/year",
    priceValue: "AI Chatbot",
    icon: Bot,
    bestFor: "Existing websites that need intelligent customer support",
    description: "Standalone chatbot for your website",
    features: [
      "Chatbot setup for FAQs + lead capture",
      "Trained on your content (docs, FAQs, services)",
      "Routing to email + conversation transcripts",
      "Conversational SEO scripts for users' intent"
    ],
    notIncluded: null,
    cta: "Choose AI Chatbot"
  },
  {
    name: "Website + SEO + Chatbot",
    price: "$1,000/year",
    priceValue: "Website + SEO + Chatbot",
    icon: Settings,
    bestFor: "Complete solution with website, SEO, and smart chatbot",
    description: "Website Essentials + monthly SEO + AI Chatbot included",
    features: [
      "Website Essentials + monthly SEO + AI Chatbot included",
      "Same details as Growth + SEO plus the chatbot features above"
    ],
    notIncluded: null,
    cta: "Choose Website + SEO + Chatbot"
  },
  {
    name: "Custom App / Web App / MVP",
    price: "Custom (request quote)",
    priceValue: "Custom App / Web App / MVP",
    icon: Settings,
    bestFor: "SaaS founders and teams who need apps that solve real business problems",
    description: "Full-scale application development with proven process",
    features: [
      "Web apps, mobile apps, automations/integrations",
      "Process: Strategy → UX/UI sprints → Build & weekly demos → Test & launch"
    ],
    notIncluded: null,
    cta: "Request Quote"
  }
];

const faqs = [
  {
    question: "What's included in each plan?",
    answer: "Each plan includes everything listed in its features. Website plans include hosting, SSL, and ongoing support. Custom app projects include full development, design, and launch support."
  },
  {
    question: "Can I add more pages or features?",
    answer: "Yes! Additional pages and features can be added to any website plan. We'll provide a clear quote for any add-ons before starting work."
  },
  {
    question: "How do updates and content changes work?",
    answer: "Website Essentials includes 1 hour of content updates per month. Growth + SEO includes 2 hours. Larger changes are scoped separately with transparent pricing."
  },
  {
    question: "What about domain and email hosting?",
    answer: "All website plans include web hosting and SSL. Domain registration and email hosting are additional services we can help you set up."
  },
  {
    question: "Can I switch between plans?",
    answer: "Absolutely! You can upgrade or downgrade at any time. We'll pro-rate the difference and adjust your billing accordingly."
  },
  {
    question: "How is onboarding handled?",
    answer: "After choosing a plan, we'll schedule a discovery call to understand your needs, then provide a detailed project timeline and next steps within 24 hours."
  }
];

const Pricing = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  // Set page meta tags when component mounts
  useEffect(() => {
    setPricingPageMeta();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Pricing - Web Development & Design Packages | AffinityAim"
        description="Transparent web development pricing. Starter, Growth, and Enterprise packages. Custom quotes for AI integration and complex projects."
        canonical="https://www.affinityaim.com/pricing"
        keywords="website pricing, annual website plans, chatbot pricing, web app development cost, custom app pricing"
        schema={{
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          "name": "AffinityAim Pricing"
        }}
      />
      <Navigation />

      <main id="main-content">
        {/* Hero */}
        <section className="section-spacing bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <div className="mb-16">
                <div className="micro-label mb-6">PRICING</div>
                <h1 className="font-display font-bold text-foreground mb-6" style={{ fontSize: '56px', lineHeight: '1.1', letterSpacing: '-0.01em' }}>
                  Simple, transparent pricing
                  <br />
                  <span className="bg-gradient-primary bg-clip-text text-transparent">
                    for every business
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground max-w-paragraph mx-auto">
                  5 annual plans for websites, chatbots, and custom apps. No hidden fees, no surprises.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Pricing Models */}
        <section className="pb-20 bg-background">
          <div className="container mx-auto px-6">
            <div ref={elementRef} className="max-w-content-wide mx-auto">
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {pricingPlans.map((plan, index) => {
                  const IconComponent = plan.icon;
                  const isCustom = plan.priceValue === 'custom';
                  const isPopular = index === 1;
                  return (
                    <div 
                      key={index}
                      className={`card-matte p-8 hover-lift tap-target relative ${
                        isPopular ? 'border-primary/40 bg-primary/5' : ''
                      }`}
                      style={getRevealStyle(index)}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                            Most Popular
                          </div>
                        </div>
                      )}
                      
                      {/* Header */}
                      <div className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-display font-semibold text-foreground" style={{ fontSize: '18px', lineHeight: '1.3' }}>
                              {plan.name}
                            </h3>
                          </div>
                        </div>
                        
                        <div className="text-2xl font-bold text-foreground mb-3">
                          {plan.price}
                        </div>
                        
                        <p className="text-sm text-primary font-medium mb-4">
                          {plan.bestFor}
                        </p>
                        
                        <p className="text-muted-foreground leading-relaxed text-sm">
                          {plan.description}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="mb-8">
                        <div className="space-y-3">
                          {plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>
                        {plan.notIncluded && (
                          <div className="mt-4 p-3 bg-background-secondary rounded-lg">
                            <div className="text-xs font-medium text-foreground mb-1">Not included:</div>
                            <div className="text-xs text-muted-foreground">{plan.notIncluded}</div>
                          </div>
                        )}
                      </div>

                      {/* CTA */}
                      <Link to={`/contact?plan=${encodeURIComponent(plan.priceValue)}`}>
                        <Button variant={isPopular ? "default" : "outline"} className="w-full group border-stroke-hairline hover:border-stroke-strong tap-target">
                          {plan.cta}
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>

                    </div>
                  );
                })}
              </div>
              
              <div className="text-center mt-8">
                <p className="text-sm text-muted-foreground">
                  Exact scope and any add-ons are confirmed after a short call.
                </p>
              </div>

            </div>
          </div>
        </section>

      {/* FAQs */}
      <section className="py-20 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                Common questions
              </h2>
              <p className="text-muted-foreground">
                Everything you need to know about working with us.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="card-matte">
                  <button
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-surface/50 transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-lg tap-target"
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    aria-expanded={expandedFAQ === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="font-medium text-foreground">{faq.question}</span>
                    <ArrowRight 
                      className={`w-5 h-5 text-muted-foreground transition-transform ${
                        expandedFAQ === index ? 'rotate-90' : ''
                      }`} 
                    />
                  </button>
                  
                  {expandedFAQ === index && (
                    <div id={`faq-answer-${index}`} className="px-6 pb-6">
                      <div className="h-px bg-stroke-hairline mb-4" />
                      <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.1' }}>
                Ready to get started?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-paragraph mx-auto">
                Book a 15-minute call to discuss your project and get a custom estimate.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target">
                    Book strategy call
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground tap-target">
                    View our work
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;